import { MouseEvent, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { find } from 'lodash-es';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'src/auth/useAuth';
import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import Empty from 'src/common/components/Empty';
import {
  dashboardPrimaryActionButtonProps,
} from 'src/common/components/DashboardPageToolbar';
import { ticketCardScrollContainerSx } from 'src/common/styles/ticketCardStyles';
import UserCheckinWizardOpenButton from 'src/common/components/UserCheckinWizardOpenButton';
import { EOrder } from 'src/interfaces/Order';
import { IUser } from 'src/interfaces/User';
import { EView } from 'src/interfaces/View';
import usePersistentView from 'src/hooks/usePersistentView';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import useUsersStore from 'src/stores/usersStore';

import { EUserOrderBy } from 'src/common/constants/userSortKeys';
import { filterUsers } from 'src/helpers/filterUsers';
import { IFormAdminUser } from 'src/interfaces/User';
import UsersFilterForm from './forms/UsersFilterForm';
import UsersGrid from './components/UsersGrid';
import UserEditorModal from './modals/UserEditorModal';
import UsersTable from './tables/UsersTable';
import UserCheckinWizardModal from 'src/pages/Dashboard/Participants/modals/UserCheckinWizardModal/UserCheckinWizardModal';

export default function Users() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin, isSuperAdmin, user: currentUser } = useAuth();
  const { activeEditionId, fetchRoles } = useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const { users, isLoading, fetchUsers, createUser, updateUser, deleteUser, deleteAllUsers } =
    useUsersStore();

  const [view, setView] = usePersistentView();
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<EUserOrderBy>(EUserOrderBy.name);
  const [orderDir, setOrderDir] = useState<EOrder>(EOrder.asc);
  const [userEditorModalOpen, setUserEditorModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | undefined>();
  const [userCheckinWizardOpen, setUserCheckinWizardOpen] = useState(false);
  const [wizardInitialUserId, setWizardInitialUserId] = useState<string | undefined>();

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSorted = useMemo(
    () =>
      filterUsers(users, {
        query,
        roleFilter,
        orderBy,
        orderDir,
      }),
    [users, query, roleFilter, orderBy, orderDir],
  );

  const handleOpenUserEditorModal = (id?: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    if (!isSuperAdmin) return;
    setSelectedUser(find(users, { id }));
    setUserEditorModalOpen(true);
  };

  const handleCloseUserEditorModal = () => {
    setSelectedUser(undefined);
    setUserEditorModalOpen(false);
  };

  const handleOpenUserCheckinWizard = (userId?: string) => {
    setWizardInitialUserId(userId);
    setUserCheckinWizardOpen(true);
  };

  const saveUser = async (formUser: IFormAdminUser) => {
    if (!isSuperAdmin) return;
    try {
      if (formUser.id) {
        await updateUser(formUser);
      } else {
        await createUser(formUser);
      }
      enqueueSnackbar(t('users.saveSuccessful'), { variant: 'success' });
      handleCloseUserEditorModal();
    } catch (error) {
      const {
        response: {
          data: { errorCode, message },
        },
      } = error as { response: { data: { errorCode?: string; message?: string } } };
      enqueueSnackbar(errorCode ? t(`users.errors.${errorCode}`, { defaultValue: message }) : message, {
        variant: 'error',
      });
    }
  };

  const handleDeleteUser = async (id: string, e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    if (!isSuperAdmin) return;
    if (id === currentUser?.id) {
      enqueueSnackbar(t('users.cannotDeleteSelf'), { variant: 'warning' });
      return;
    }
    const { confirmed } = await confirm({
      title: t('users.deleteConfirm'),
      description: t('users.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      return;
    }
    await deleteUser(id);
    enqueueSnackbar(t('users.deleteSuccessfulMessage'), { variant: 'success' });
  };

  const handleDeleteAllUsers = async (e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    const { confirmed } = await confirm({
      title: t('users.deleteAllConfirm'),
      description: t('users.deleteDescription'),
      confirmationText: t('confirmProvider.confirmationText'),
      cancellationText: t('confirmProvider.cancellationText'),
    });
    if (!confirmed) {
      return;
    }
    await deleteAllUsers();
    enqueueSnackbar(t('users.deleteSuccessfulMessage'), { variant: 'success' });
  };

  return (
    <>
      <DashboardListPageLayout
        title={`${t('drawer.users')} (${filteredSorted.length})`}
        isLoading={isLoading}
        headerActions={
          <>
            {isSuperAdmin ? (
            <>
              <Tooltip title={t('users.deleteAllUsers')}>
                <IconButton
                  onClick={handleDeleteAllUsers}
                  color="error"
                  aria-label={t('users.deleteAllUsers')}
                >
                  <DeleteForeverIcon />
                </IconButton>
              </Tooltip>
              <Button
                {...dashboardPrimaryActionButtonProps()}
                onClick={() => handleOpenUserEditorModal()}
                startIcon={<AddIcon />}
              >
                {t('users.new')}
              </Button>
            </>
            ) : null}
            {isAdmin ? (
              <UserCheckinWizardOpenButton
                activeEditionId={activeEditionId}
                onClick={() => handleOpenUserCheckinWizard()}
              />
            ) : null}
          </>
        }
        toolbar={
          <UsersFilterForm
            query={query}
            onQuery={setQuery}
            roleFilter={roleFilter}
            onRoleFilter={setRoleFilter}
            order={orderDir}
            orderByKey={orderBy}
            setOrder={setOrderDir}
            setOrderByKey={setOrderBy}
            view={view}
            onSetView={setView}
          />
        }
      >
        {!isLoading && users.length === 0 && (
          <Empty
            icon={<PersonOutlinedIcon />}
            title={t('empty.users.title')}
            description={t('empty.users.description')}
            primaryAction={{
              label: t('empty.addNew'),
              onClick: () => handleOpenUserEditorModal(),
            }}
          />
        )}
        {!isLoading && users.length > 0 && filteredSorted.length === 0 && (
          <ListFilterEmptyState />
        )}
        {filteredSorted.length > 0 && (
          <Box sx={ticketCardScrollContainerSx(view === EView.grid)}>
            {view === EView.grid && (
              <UsersGrid
                users={filteredSorted}
                canManage={isSuperAdmin}
                showViewParticipants={isAdmin}
                onDelete={handleDeleteUser}
                onEdit={handleOpenUserEditorModal}
              />
            )}
            {view === EView.table && (
              <UsersTable
                users={filteredSorted}
                canManage={isSuperAdmin}
                onCheckIn={isAdmin ? handleOpenUserCheckinWizard : undefined}
                onDelete={handleDeleteUser}
                onEdit={handleOpenUserEditorModal}
              />
            )}
          </Box>
        )}
      </DashboardListPageLayout>

      {isSuperAdmin && userEditorModalOpen && (
        <UserEditorModal
          id="user-editor"
          open={userEditorModalOpen}
          handleClose={handleCloseUserEditorModal}
          handleSave={saveUser}
          user={selectedUser}
        />
      )}

      {isAdmin && activeEditionId && (
        <UserCheckinWizardModal
          open={userCheckinWizardOpen}
          editionId={activeEditionId}
          initialUserId={wizardInitialUserId}
          onClose={() => {
            setUserCheckinWizardOpen(false);
            setWizardInitialUserId(undefined);
          }}
        />
      )}
    </>
  );
}
